import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  ChevronDown, 
  Zap, 
  Clock, 
  Settings, 
  Shield, 
  Globe,
  TrendingUp,
  Package,
  Truck,
  Factory,
  BarChart3,
  CheckCircle2,
  Play,
  ArrowUpRight
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const stats = [
  { value: 'Scalable', suffix: '', label: 'Warehouse Infrastructure' },
  { value: 'Flexible', suffix: '', label: 'Rental-Based Solutions' },
  { value: 'Built for', suffix: '', label: "India's Logistics Economy" },
  { value: 'Pan-India', suffix: '', label: 'Major Logistics Hubs' },
];

const problems = [
  { icon: TrendingUp, text: 'Heavy upfront CAPEX', detail: 'Large capital investments required' },
  { icon: Clock, text: 'Long installation timelines', detail: '3-6 months deployment time' },
  { icon: Package, text: 'Fragmented vendor coordination', detail: 'Multiple vendors to manage' },
  { icon: Shield, text: 'Limited flexibility for scaling', detail: 'Rigid infrastructure locks' },
];

const solutions = [
  { 
    title: 'Modular Fit-out Systems', 
    description: 'Pre-engineered, quick-deploy infrastructure that adapts to your needs',
    icon: '01'
  },
  { 
    title: 'Automation-Ready Design', 
    description: 'Future-proof infrastructure ready for robotics, ASRS & WMS integration',
    icon: '02'
  },
  { 
    title: 'Energy-Efficient Upgrades', 
    description: 'LED lighting, solar-ready systems, and smart energy monitoring',
    icon: '03'
  },
  { 
    title: 'Integrated Service Support', 
    description: 'Single partner for complete lifecycle management and maintenance',
    icon: '04'
  },
];

const whyPoints = [
  { 
    icon: Zap, 
    title: 'Reduce CAPEX', 
    description: 'Convert capital expenditure into predictable operating expense with flexible rentals.',
    metric: '40%',
    metricLabel: 'Cost Savings'
  },
  { 
    icon: Clock, 
    title: 'Faster Deployment', 
    description: 'Pre-engineered, modular systems reduce setup time from months to weeks.',
    metric: '60%',
    metricLabel: 'Faster Setup'
  },
  { 
    icon: Settings, 
    title: 'Integrated Partner', 
    description: 'Beyond fit-outs — automation readiness, energy upgrades, security, and lifecycle support.',
    metric: '1',
    metricLabel: 'Single Vendor'
  },
  { 
    icon: Shield, 
    title: 'Tech-Driven', 
    description: 'IoT-ready, automation-ready, and future-proof designs built for Industry 4.0.',
    metric: '100%',
    metricLabel: 'IoT Ready'
  },
  { 
    icon: Globe, 
    title: 'Pan-India Reach', 
    description: 'Consistent service capability across all major logistics hubs nationwide.',
    metric: '50+',
    metricLabel: 'Cities'
  },
];

const trustedBy = [
  'Leading eCommerce', '3PL Giants', 'Manufacturing', 'FMCG Brands', 'Pharma Leaders'
];

// Animated counter hook
const useCountUp = (end, duration = 2000) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, [end, duration]);

  return [count, countRef];
};

const StatCounter = ({ value, suffix, label }) => {
  return (
    <div className="stats-item text-center group">
      <div className="font-mono text-2xl md:text-3xl font-bold mb-2">
        <span className="gradient-text-gold">{value}</span>
        <span className="text-[#E63946]">{suffix}</span>
      </div>
      <div className="text-xs text-gray-500 uppercase tracking-[0.15em] group-hover:text-gray-400 transition-colors">
        {label}
      </div>
    </div>
  );
};

export default function Home() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div className="bg-[#0a1628]" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section relative min-h-screen flex items-center" data-testid="hero-section">
        {/* Animated Background Grid */}
        <div className="absolute inset-0 animated-grid" />
        
        {/* Background Image with Parallax */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed opacity-40"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1720036236697-018370867320?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhdXRvbWF0ZWQlMjB3YXJlaG91c2UlMjBpbnRlcmlvciUyMGRhcmslMjBjaW5lbWF0aWMlMjBuaWdodHxlbnwwfHx8fDE3NzE4MjI5Mzh8MA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        
        {/* Multiple Gradient Overlays for Depth */}
        <div className="absolute inset-0 gradient-overlay" />
        <div className="absolute inset-0 gradient-overlay-left" />
        
        {/* Radial Glow */}
        <div className="absolute top-1/4 left-1/4 w-[800px] h-[800px] bg-[#E63946] opacity-[0.03] blur-[150px] rounded-full" />
        <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-[#E63946] opacity-[0.02] blur-[150px] rounded-full" />
        
        <div className="container-custom relative z-10 pt-32 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className={`lg:col-span-7 ${isLoaded ? 'animate-fade-in-up' : 'opacity-0'}`}>
              {/* Large Logo for Branding */}
              <div className="mb-10">
                <div className="bg-white rounded-lg px-6 py-4 inline-block shadow-2xl shadow-white/10">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/vjaqzlc7_IMG_0255.jpeg" 
                    alt="Warehaus+ - Designed to Store. Built to Scale."
                    className="h-16 md:h-20 lg:h-24 w-auto object-contain"
                  />
                </div>
              </div>

              {/* Premium Badge */}
              <div className="premium-badge mb-8">
                <span className="w-2 h-2 bg-[#E63946] rounded-full animate-pulse" />
                <span className="font-mono text-xs text-[#E63946] uppercase tracking-[0.2em]">
                  Warehouse-as-a-Service
                </span>
              </div>

              {/* Main Headline */}
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-bold text-white uppercase leading-[0.9] tracking-tight mb-8">
                Infrastructure That Moves at the
                <span className="block text-[#E63946] text-glow-gold mt-2">
                  Speed of Business
                </span>
              </h1>

              {/* Subheading */}
              <p className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed mb-12">
                Warehaus+ delivers modular warehouse fit-outs and integrated services on a 
                <span className="text-white font-medium"> flexible rental model</span> — enabling 
                faster deployment, lower upfront investment, and scalable growth.
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap gap-5">
                <Link to="/products" data-testid="hero-cta-explore">
                  <Button className="btn-primary magnetic-btn clip-slant h-14 px-10 text-sm">
                    Explore Our Solutions
                    <ArrowRight className="ml-3 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/contact" data-testid="hero-cta-partner">
                  <Button className="btn-secondary magnetic-btn h-14 px-10 text-sm">
                    Partner With Us
                  </Button>
                </Link>
              </div>

              {/* Trusted By Section */}
              <div className="mt-16 pt-8 border-t border-white/5">
                <p className="text-xs text-gray-600 uppercase tracking-[0.2em] mb-4">Trusted by Industry Leaders</p>
                <div className="flex flex-wrap gap-6">
                  {trustedBy.map((item, index) => (
                    <span key={index} className="text-sm text-gray-500 font-medium">{item}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats Panel */}
            <div className={`lg:col-span-5 ${isLoaded ? 'animate-fade-in-up delay-300' : 'opacity-0'}`}>
              <div className="industrial-card gold-corners glass-card p-8 lg:p-10">
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <StatCounter key={index} {...stat} />
                  ))}
                </div>
                
                {/* Quick Action */}
                <div className="mt-8 pt-8 border-t border-white/5">
                  <Link to="/contact" className="group flex items-center justify-between p-4 bg-[#E63946]/5 border border-[#E63946]/20 hover:border-[#E63946]/40 transition-all">
                    <div>
                      <p className="text-white font-medium text-sm">Get in Touch</p>
                      <p className="text-gray-500 text-xs mt-1">Response within 24 hours</p>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-[#E63946] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 scroll-indicator">
            <div className="flex flex-col items-center gap-2">
              <span className="text-xs text-gray-600 uppercase tracking-widest">Scroll</span>
              <ChevronDown className="w-5 h-5 text-gray-500" />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-32 md:py-40 relative overflow-hidden" data-testid="problem-section">
        <div className="absolute inset-0 tech-grid-bg opacity-50" />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-transparent via-[#E63946]/20 to-transparent" />
        
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <span className="font-mono text-xs text-red-500/80 uppercase tracking-[0.2em] mb-6 block">
                // The Challenge
              </span>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-8 leading-[1.1]">
                Warehousing is Evolving —
                <span className="block text-gray-600 mt-2">But Infrastructure Remains Rigid</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                Traditional fit-outs demand heavy investment and long timelines, leaving businesses 
                locked into inflexible infrastructure that can't adapt to changing needs.
              </p>
              <div className="p-6 bg-red-500/5 border-l-2 border-red-500/50">
                <p className="text-white font-medium">
                  Businesses today need infrastructure that adapts — not infrastructure that locks them in.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {problems.map((problem, index) => (
                <div
                  key={index}
                  className="group industrial-card bg-[#0a0a0a] border border-white/5 p-6 hover:border-red-500/30 transition-all duration-500"
                  data-testid={`problem-card-${index}`}
                >
                  <problem.icon className="w-8 h-8 text-red-500/50 mb-5 group-hover:text-red-500 transition-colors duration-300" />
                  <h3 className="text-white font-semibold mb-2">{problem.text}</h3>
                  <p className="text-gray-600 text-sm">{problem.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-32 md:py-40 bg-[#0c1e35] relative overflow-hidden" data-testid="solution-section">
        {/* Background Decoration */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-[#E63946] opacity-[0.02] blur-[200px] rounded-full" />
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-20">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-[0.2em] mb-6 block">
              // The Warehaus+ Solution
            </span>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-6">
              Modular. Scalable. <span className="gradient-text-blue">Intelligent.</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
              Warehaus+ transforms empty warehouse shells into fully operational, future-ready 
              facilities through our comprehensive Warehouse-as-a-Service approach.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {solutions.map((solution, index) => (
              <div
                key={index}
                className="group premium-card industrial-card bg-[#0a1628] border border-white/5 p-8 hover:border-[#E63946]/30"
                data-testid={`solution-card-${index}`}
              >
                <div className="font-mono text-4xl font-bold text-[#E63946]/20 group-hover:text-[#E63946]/40 transition-colors mb-6">
                  {solution.icon}
                </div>
                <h3 className="font-heading text-xl font-bold text-white uppercase mb-4 group-hover:text-[#E63946] transition-colors">
                  {solution.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {solution.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-6 px-10 py-5 bg-[#E63946]/5 border border-[#E63946]/20">
              <span className="text-[#E63946] font-heading text-xl md:text-2xl font-bold uppercase tracking-wider">
                We call it Warehouse-as-a-Service
              </span>
              <ArrowRight className="w-6 h-6 text-[#E63946]" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Warehaus+ Section */}
      <section className="py-32 md:py-40 relative" data-testid="why-section">
        <div className="absolute inset-0 tech-grid-bg opacity-30" />
        
        <div className="container-custom relative z-10">
          <div className="flex flex-col lg:flex-row gap-20">
            <div className="lg:w-2/5 lg:sticky lg:top-32 lg:self-start">
              <span className="font-mono text-xs text-[#E63946] uppercase tracking-[0.2em] mb-6 block">
                // Why Choose Us
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
                Why Warehaus<span className="text-[#E63946]">+</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                Built for the modern warehouse economy. We provide enterprise-grade infrastructure 
                solutions that scale with your business.
              </p>
              <Link to="/why-warehaus" data-testid="why-learn-more">
                <Button className="btn-secondary h-12 px-8 text-sm">
                  Learn More <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="lg:w-3/5">
              <div className="space-y-6">
                {whyPoints.map((point, index) => (
                  <div
                    key={index}
                    className="group premium-card p-8 border border-white/5 hover:border-[#E63946]/30 bg-[#0a1628]/50 transition-all duration-500"
                    data-testid={`why-card-${index}`}
                  >
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-14 h-14 bg-[#E63946]/10 flex items-center justify-center group-hover:bg-[#E63946]/20 transition-colors">
                          <point.icon className="w-7 h-7 text-[#E63946]" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-heading text-xl font-bold text-white uppercase group-hover:text-[#E63946] transition-colors">
                            {point.title}
                          </h3>
                          <div className="text-right">
                            <div className="font-mono text-2xl font-bold text-[#E63946]">{point.metric}</div>
                            <div className="text-xs text-gray-600 uppercase tracking-wider">{point.metricLabel}</div>
                          </div>
                        </div>
                        <p className="text-gray-500 leading-relaxed">
                          {point.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Market Opportunity Section */}
      <section className="py-32 md:py-40 bg-[#0c1e35] relative overflow-hidden" data-testid="market-section">
        <div className="absolute top-0 right-0 w-2/3 h-full">
          <img
            src="https://images.unsplash.com/photo-1720036236694-d0a231c52563?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwcmFja2luZyUyMHN5c3RlbSUyMHN0ZWVsJTIwbWV6emFuaW5lJTIwYXV0b21hdGlvbnxlbnwwfHx8fDE3NzE4MjI5NDB8MA&ixlib=rb-4.1.0&q=85"
            alt="Warehouse"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0c1e35] via-[#0c1e35]/90 to-transparent" />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-2xl">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-[0.2em] mb-6 block">
              // Market Opportunity
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-8 leading-[1.1]">
              India's Grade A Warehousing is
              <span className="block gradient-text-blue mt-2">Projected to Grow</span>
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-10">
              Driven by the explosive growth of eCommerce, 3PL expansion, manufacturing push, 
              and supply chain formalization, India's warehousing sector presents unprecedented opportunity.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {[
                { icon: Package, name: 'eCommerce' },
                { icon: Truck, name: '3PL Providers' },
                { icon: Factory, name: 'Manufacturing' },
                { icon: BarChart3, name: 'Distribution' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 bg-[#0a1628] border border-white/5 hover:border-[#E63946]/30 transition-colors"
                >
                  <item.icon className="w-5 h-5 text-[#E63946]" />
                  <span className="text-gray-400 text-sm">{item.name}</span>
                </div>
              ))}
            </div>

            <div className="p-6 bg-[#0a1628] border-l-2 border-[#E63946]">
              <p className="text-white font-medium">
                Warehaus+ is positioned to capture this opportunity with scalable, asset-light infrastructure solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 md:py-40 relative cta-section" data-testid="cta-section">
        <div className="container-custom relative z-10">
          <div className="industrial-card gold-corners bg-[#0a1628] border border-[#E63946]/20 p-12 md:p-20">
            <div className="max-w-3xl mx-auto text-center">
              <span className="font-mono text-xs text-[#E63946] uppercase tracking-[0.2em] mb-6 block">
                // Get Started
              </span>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-8">
                Ready to Transform Your
                <span className="block text-[#E63946] text-glow-gold mt-2">Warehouse Infrastructure?</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-12">
                Get in touch with our experts to discuss your requirements and discover how 
                Warehaus+ can help you scale efficiently.
              </p>
              <div className="flex flex-wrap justify-center gap-5">
                <Link to="/contact" data-testid="cta-contact">
                  <Button className="btn-gold magnetic-btn clip-slant h-14 px-12 text-base">
                    Contact Us
                    <ArrowRight className="ml-3 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/products" data-testid="cta-products">
                  <Button className="btn-secondary magnetic-btn h-14 px-12 text-base">
                    Explore Products
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
